<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'customers' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">{{ customer.name || 'Détails du client' }}</h1>
          <p v-if="customer.id" class="text-xs text-gray-400">
            Membre depuis {{ formatDate(customer.created_at) }}
          </p>
        </div>
      </div>
      <div v-if="customer.id" class="flex items-center gap-2">
        <button v-permission="'customers'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="sendSMS">
          <i class="fas fa-comment-sms text-[11px]"></i>
          SMS
        </button>
        <button v-permission="'customers'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="sendEmail">
          <i class="fas fa-envelope text-[11px]"></i>
          Email
        </button>
        <button v-permission="'customers.edit'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="editCustomer">
          <i class="fas fa-pen text-[11px]"></i>
          Modifier
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading"
      class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement...</p>
    </div>

    <!-- Client introuvable -->
    <div v-else-if="!customer.id"
      class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Client introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">Le client que vous recherchez n'existe pas.</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'customers' })">
        Retour aux clients
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte infos client -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div class="flex items-center gap-4">
              <div class="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-user text-primary text-xl"></i>
              </div>
              <div>
                <h2 class="text-base font-bold text-gray-900 uppercase">{{ customer.name }}</h2>
                <p v-if="customer.company" class="text-xs text-gray-400 mt-0.5">{{ customer.company }}</p>
              </div>
            </div>
            <span :class="[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
              customer.status ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
            ]">
              <span class="w-1.5 h-1.5 rounded-full" :class="customer.status ? 'bg-green-500' : 'bg-gray-400'"></span>
              {{ customer.status ? 'Actif' : 'Inactif' }}
            </span>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div v-if="customer.phone">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Téléphone</p>
              <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <i class="fas fa-phone text-[9px] text-gray-300"></i>
                {{ customer.phone }}
              </p>
            </div>
            <div v-if="customer.email">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Email</p>
              <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <i class="fas fa-envelope text-[9px] text-gray-300"></i>
                {{ customer.email }}
              </p>
            </div>
            <div v-if="customer.full_address">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Adresse</p>
              <p class="text-sm font-medium text-gray-700">{{ customer.full_address }}</p>
            </div>
            <div v-if="!customer.phone && !customer.email && !customer.full_address">
              <p class="text-xs text-gray-300 italic">Aucune information de contact</p>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div>
          <!-- Tab navigation -->
          <div class="flex gap-1 p-1 bg-white rounded-xl border border-gray-200 mb-5">
            <button v-for="tab in tabs" :key="tab.key"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition-colors"
              :class="activeTab === tab.key
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
              @click="activeTab = tab.key">
              <i :class="tab.icon" class="text-[11px]"></i>
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab content -->
          <CustomerSalesHistory
            v-if="activeTab === 'sales'"
            :customer-id="customer.id"
            :wallet="wallet"
            :customer="customer"
            @refresh="loadCustomerDetails"
          />
          <CustomerSaleInvoice
            v-if="activeTab === 'invoices'"
            :customer-id="customer.id"
          />
          <CustomerWalletTransactions
            v-if="activeTab === 'transactions' && wallet?.id"
            :wallet-id="wallet.id"
          />
          <div v-if="activeTab === 'transactions' && !wallet?.id"
            class="bg-white rounded-xl border border-gray-200 py-12 text-center">
            <div class="text-gray-300 mb-3">
              <i class="fas fa-wallet text-3xl"></i>
            </div>
            <p class="text-sm text-gray-500">Aucun portefeuille disponible</p>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Portefeuille -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b bg-violet-50 border-violet-100">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-semibold text-violet-700">Portefeuille</h3>
              <button
                class="w-7 h-7 flex items-center justify-center bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors text-[10px]"
                title="Créditer le portefeuille"
                @click="openCreditWalletModal">
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
          <div class="p-5">
            <div v-if="walletLoading" class="flex items-center gap-2 text-xs text-gray-400">
              <i class="fas fa-spinner fa-spin"></i> Chargement...
            </div>
            <div v-else class="text-center">
              <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(wallet?.balance || 0) }}</p>
              <p class="text-xs text-gray-400 mt-0.5">Solde disponible</p>
            </div>
          </div>
        </div>

        <!-- Statistiques -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Statistiques</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-cart-shopping text-blue-500 text-[10px]"></i>
                </span>
                Nombre de ventes
              </span>
              <span class="text-sm font-bold text-gray-900">{{ customer.sales_count || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-coins text-green-500 text-[10px]"></i>
                </span>
                Total payé
              </span>
              <span class="text-sm font-bold text-green-600">{{ formatCurrency(customer.total_spent || 0) }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="customer.total_unpaid > 0 ? 'bg-orange-50' : 'bg-gray-50'">
                  <i class="fas fa-clock text-[10px]"
                    :class="customer.total_unpaid > 0 ? 'text-orange-500' : 'text-gray-400'"></i>
                </span>
                Impayés
              </span>
              <div class="text-right">
                <span class="text-sm font-bold" :class="customer.total_unpaid > 0 ? 'text-orange-600' : 'text-gray-400'">
                  {{ formatCurrency(customer.total_unpaid || 0) }}
                </span>
                <p v-if="customer.unpaid_sales_count > 0" class="text-[10px] text-gray-400">
                  {{ customer.unpaid_sales_count }} facture(s)
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Statut du client -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-gray-700">Statut du client</h3>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ customer.status ? 'Le client est actif' : 'Le client est inactif' }}
              </p>
            </div>
            <button
              class="relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none"
              :class="customer.status ? 'bg-primary' : 'bg-gray-300'"
              :disabled="isTogglingStatus"
              @click="openStatusConfirmModal">
              <span
                class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                :class="customer.status ? 'translate-x-5' : 'translate-x-0'"></span>
            </button>
          </div>
        </div>

        <!-- Actions rapides -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
          <div class="space-y-1.5">
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="editCustomer">
              <i class="fas fa-pen text-xs w-4 text-center"></i>
              Modifier les informations
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="sendSMS">
              <i class="fas fa-comment-sms text-xs w-4 text-center"></i>
              Envoyer un SMS
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="sendEmail">
              <i class="fas fa-envelope text-xs w-4 text-center"></i>
              Envoyer un email
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="openCreditWalletModal">
              <i class="fas fa-wallet text-xs w-4 text-center"></i>
              Créditer le portefeuille
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de crédit du portefeuille -->
    <CreditWalletModal
      :show="showCreditWalletModal"
      :customer-id="customer.id"
      :customer-name="customer.name"
      :current-balance="wallet?.balance || 0"
      @close="closeCreditWalletModal"
      @credit-added="onCreditAdded"
    />

    <!-- Modal de confirmation changement de statut -->
    <Teleport to="body">
      <transition name="modal">
        <div v-if="showStatusConfirmModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="closeStatusConfirmModal">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              :class="customer.status ? 'bg-amber-50' : 'bg-green-50'">
              <i :class="customer.status ? 'fas fa-exclamation-circle text-amber-500' : 'fas fa-check-circle text-green-500'"
                class="text-2xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-2">
              {{ customer.status ? 'Désactiver le client ?' : 'Activer le client ?' }}
            </h3>
            <p class="text-sm text-gray-500 mb-6 leading-relaxed">
              {{ customer.status
                ? `"${customer.name}" ne pourra plus être sélectionné pour de nouvelles ventes.`
                : `"${customer.name}" sera de nouveau disponible pour les ventes.`
              }}
            </p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                :disabled="isTogglingStatus"
                @click="closeStatusConfirmModal">
                Annuler
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                :class="customer.status ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary-dark'"
                :disabled="isTogglingStatus"
                @click="confirmToggleStatus">
                <i v-if="isTogglingStatus" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
                {{ customer.status ? 'Désactiver' : 'Activer' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import customerService from '@/services/customerService'
import CustomerSalesHistory from '@/components/customers/CustomerSalesHistory.vue'
import CustomerSaleInvoice from '@/components/customers/CustomerSaleInvoice.vue'
import CreditWalletModal from '@/components/customers/CreditWalletModal.vue'
import CustomerWalletTransactions from '@/components/customers/CustomerWalletTransactions.vue'

export default defineComponent({
  name: 'CustomerDetail',
  components: {
    CustomerSalesHistory,
    CustomerSaleInvoice,
    CreditWalletModal,
    CustomerWalletTransactions
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const customer = ref({})
    const loading = ref(true)
    const activeTab = ref('sales')

    const tabs = [
      { key: 'sales', label: 'Ventes', icon: 'fas fa-cart-shopping' },
      { key: 'invoices', label: 'Facturations', icon: 'fas fa-file-invoice' },
      { key: 'transactions', label: 'Transactions', icon: 'fas fa-wallet' }
    ]

    // Wallet
    const wallet = ref(null)
    const walletLoading = ref(false)
    const showCreditWalletModal = ref(false)

    // Status toggle
    const showStatusConfirmModal = ref(false)
    const isTogglingStatus = ref(false)

    const loadCustomerDetails = async () => {
      const customerId = route.params.id
      if (!customerId) {
        router.push({ name: 'customers' })
        return
      }

      try {
        loading.value = true
        const response = await customerService.getCustomer(customerId)
        customer.value = response.data.customer || response.data
        loadWallet()
      } catch (error) {
        console.error('Erreur:', error)
        customer.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadWallet = async () => {
      const customerId = route.params.id
      if (!customerId) return

      try {
        walletLoading.value = true
        const response = await customerService.getCustomerWallet(customerId)
        wallet.value = response.data.data
      } catch (error) {
        console.error('Erreur lors du chargement du portefeuille:', error)
        wallet.value = null
      } finally {
        walletLoading.value = false
      }
    }

    const openCreditWalletModal = () => {
      showCreditWalletModal.value = true
    }

    const closeCreditWalletModal = () => {
      showCreditWalletModal.value = false
    }

    const onCreditAdded = () => {
      loadWallet()
      closeCreditWalletModal()
    }

    const editCustomer = () => {
      router.push({ name: 'customer-edit', params: { id: customer.value.id } })
    }

    const sendSMS = () => {
      console.log('TODO: Envoyer SMS au client', customer.value.id)
    }

    const sendEmail = () => {
      console.log('TODO: Envoyer Email au client', customer.value.id)
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const openStatusConfirmModal = () => {
      showStatusConfirmModal.value = true
    }

    const closeStatusConfirmModal = () => {
      showStatusConfirmModal.value = false
    }

    const confirmToggleStatus = async () => {
      if (isTogglingStatus.value) return

      isTogglingStatus.value = true
      try {
        if (customer.value.status) {
          await customerService.deactivateCustomer(customer.value.id)
        } else {
          await customerService.activateCustomer(customer.value.id)
        }
        customer.value.status = !customer.value.status
        closeStatusConfirmModal()
      } catch (error) {
        console.error('Erreur lors du changement de statut:', error)
      } finally {
        isTogglingStatus.value = false
      }
    }

    onMounted(() => {
      loadCustomerDetails()
    })

    return {
      customer,
      loading,
      activeTab,
      tabs,
      wallet,
      walletLoading,
      showCreditWalletModal,
      showStatusConfirmModal,
      isTogglingStatus,
      loadCustomerDetails,
      loadWallet,
      openCreditWalletModal,
      closeCreditWalletModal,
      onCreditAdded,
      openStatusConfirmModal,
      closeStatusConfirmModal,
      confirmToggleStatus,
      editCustomer,
      sendSMS,
      sendEmail,
      formatCurrency,
      formatDate
    }
  }
})
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div {
  transition: transform 0.2s ease;
}
.modal-leave-active {
  transition: opacity 0.15s ease;
}
.modal-leave-active > div {
  transition: transform 0.15s ease;
}
.modal-enter-from {
  opacity: 0;
}
.modal-enter-from > div {
  transform: scale(0.95) translateY(-8px);
}
.modal-leave-to {
  opacity: 0;
}
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
</style>
